import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { Logo } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { TERMS, TERMS_META } from "@/data/terms";

const Note = ({ text }) => (
  <p className="flex gap-3 rounded-2xl border border-[var(--accent)]/25 bg-[var(--accent)]/[0.05] px-5 py-4 text-sm text-slate-200">
    <Info size={18} className="accent-text shrink-0 mt-0.5" />{text}
  </p>
);

const Clause = ({ c }) => {
  const [n, text, list] = c;
  if (n === "h") return <h3 className="pt-4 font-display font-bold text-lg text-white">{text}</h3>;
  if (n === "note") return <Note text={text} />;
  return (
    <div className="grid grid-cols-[3.25rem_1fr] gap-3">
      <span className="font-mono-epl text-sm accent-text pt-0.5">{n}</span>
      <div>
        <p className="text-slate-300 leading-relaxed">{text}</p>
        {list && <ul className="mt-3 space-y-2 text-slate-300 leading-relaxed" style={{ fontVariantLigatures: "none", fontFeatureSettings: '"liga" 0, "dlig" 0, "calt" 0' }}>{list.map((l) => <li key={l} className="pl-1">{l}</li>)}</ul>}
      </div>
    </div>
  );
};

const Part = ({ p }) => (
  <section id={`part-${p.n}`} data-testid={`terms-part-${p.n}`} className="scroll-mt-24 border-t border-white/10 pt-10">
    <h2 className="font-display font-bold text-2xl tracking-tight"><span className="accent-text mr-3">{p.n}.</span>{p.title}</h2>
    {p.intro && <p className="mt-3 text-slate-400">{p.intro}</p>}
    <div className="mt-6 space-y-4">{p.clauses.map((c, i) => <Clause key={`${c[0]}-${i}`} c={c} />)}</div>
    {p.note && <div className="mt-6"><Note text={p.note} /></div>}
  </section>
);

const Contents = () => (
  <nav data-testid="terms-contents" className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
    <p className="eyebrow">Contents</p>
    <ol className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {TERMS.map((p) => (
        <li key={p.n}><a href={`#part-${p.n}`} className="text-slate-300 hover:text-white transition-colors" data-testid={`terms-contents-link-${p.n}`}><span className="accent-text font-mono-epl mr-2">{p.n}</span>{p.title}</a></li>
      ))}
    </ol>
  </nav>
);

export default function TermsPage() {
  return (
    <div data-testid="terms-page">
      <div className="min-h-screen max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <Link to="/" className="flex items-center gap-3" data-testid="policy-home-link"><Logo /><span className="text-sm text-slate-400">Back to home</span></Link>
        <p className="eyebrow mt-16">Elite Performance Lab</p>
        <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-6xl">{TERMS_META.title}</h1>
        <p className="mt-4 text-lg text-slate-300">{TERMS_META.subtitle}</p>
        <p data-testid="terms-version" className="mt-2 font-mono-epl text-xs text-slate-500">{TERMS_META.version}</p>
        <div data-testid="terms-operator" className="mt-8 rounded-3xl border border-white/10 p-6 text-sm text-slate-300 space-y-1">
          <p className="eyebrow mb-3">Operator</p>
          {TERMS_META.operator.map((l) => <p key={l}>{l}</p>)}
        </div>
        <div className="mt-8"><Contents /></div>
        <div className="mt-12 space-y-12">{TERMS.map((p) => <Part key={p.n} p={p} />)}</div>
      </div>
      <Footer />
    </div>
  );
}
